import { useMemo, useState } from 'react';
import { Plus, X, Save, Loader2 } from 'lucide-react';
import supabase from '../lib/supabase';
import { Button, StatusLine } from './ui';

/**
 * Reusable seven-day hour-range editor that persists directly against a
 * Supabase table of the form:
 *   (id, <entityCol>, day_of_week, <startCol>, <endCol>, ...)
 *
 * Used for both `club_hours` (open_time/close_time) and `coach_hours`
 * (start_time/end_time).
 */

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export type HoursRow = {
  id?: string;
  day_of_week: number;
  start: string;
  end: string;
};

type Props = {
  table: 'club_hours' | 'coach_hours';
  entityCol: 'club_id' | 'coach_id';
  entityId: string;
  startCol: 'open_time' | 'start_time';
  endCol:   'close_time' | 'end_time';
  defaultStart?: string; // used when user clicks "Add window"
  defaultEnd?: string;
  helpText?: string;
};

export default function HoursEditor({
  table, entityCol, entityId, startCol, endCol,
  defaultStart = '08:00', defaultEnd = '22:00', helpText,
}: Props) {
  const [rows, setRows] = useState<HoursRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  // Initial load (one-time per entityId).
  useMemo(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from(table).select('*').eq(entityCol, entityId).order('day_of_week').order(startCol);
      if (cancelled) return;
      const mapped = (data ?? []).map((r: any) => ({
        id: r.id,
        day_of_week: r.day_of_week,
        start: (r[startCol] as string).slice(0, 5),
        end:   (r[endCol]   as string).slice(0, 5),
      }));
      setRows(mapped);
      setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [entityId, table]);

  const byDay = useMemo(() => {
    const by: Record<number, HoursRow[]> = {};
    for (let d = 0; d < 7; d++) by[d] = [];
    rows.forEach((r) => by[r.day_of_week]?.push(r));
    return by;
  }, [rows]);

  const addWindow = (day: number) => {
    setRows((prev) => [...prev, { day_of_week: day, start: defaultStart, end: defaultEnd }]);
  };

  const updateRow = (idx: number, patch: Partial<HoursRow>) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx: number) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    // Simple full-replace: delete all existing for this entity, re-insert current rows.
    const { error: delErr } = await supabase.from(table).delete().eq(entityCol, entityId);
    if (delErr) {
      setSaving(false);
      setMsg({ kind: 'error', text: delErr.message });
      return;
    }
    if (rows.length > 0) {
      const payload = rows.map((r) => ({
        [entityCol]: entityId,
        day_of_week: r.day_of_week,
        [startCol]:  r.start,
        [endCol]:    r.end,
      }));
      const { error: insErr } = await supabase.from(table).insert(payload);
      if (insErr) {
        setSaving(false);
        setMsg({ kind: 'error', text: insErr.message });
        return;
      }
    }
    setSaving(false);
    setMsg({ kind: 'success', text: 'Hours saved.' });
  };

  if (!loaded) {
    return (
      <div className="text-sm text-gray-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const globalIdx = (day: number, pos: number): number => {
    let seen = -1;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].day_of_week === day) {
        seen++;
        if (seen === pos) return i;
      }
    }
    return -1;
  };

  return (
    <div>
      {helpText && <p className="text-xs text-gray-500 mb-3">{helpText}</p>}
      <div className="divide-y divide-gray-100">
        {DAY_NAMES.map((dayLabel, dayIdx) => (
          <div key={dayIdx} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
            <div className="w-14 text-xs font-semibold uppercase tracking-wider text-gray-500 mt-2">{dayLabel}</div>
            <div className="flex-1 flex flex-wrap gap-2">
              {byDay[dayIdx].length === 0 && (
                <span className="text-xs text-gray-400 mt-2 italic">Closed</span>
              )}
              {byDay[dayIdx].map((r, pos) => {
                const gi = globalIdx(dayIdx, pos);
                return (
                  <div key={gi}
                    className="flex items-center gap-1 bg-[#c6f806]/20 border border-[#2d5016]/20 rounded-lg px-2 py-1"
                  >
                    <input type="time" value={r.start}
                      onChange={(e) => updateRow(gi, { start: e.target.value })}
                      className="text-sm bg-transparent focus:outline-none w-20 font-medium text-[#1a3010]" />
                    <span className="text-xs text-[#2d5016]/70">–</span>
                    <input type="time" value={r.end}
                      onChange={(e) => updateRow(gi, { end: e.target.value })}
                      className="text-sm bg-transparent focus:outline-none w-20 font-medium text-[#1a3010]" />
                    <button onClick={() => removeRow(gi)}
                      className="text-[#2d5016]/60 hover:text-red-600 ml-1" aria-label="Remove window">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
              <button onClick={() => addWindow(dayIdx)}
                className="text-xs text-[#2d5016] hover:underline flex items-center gap-1 mt-1.5 font-medium">
                <Plus className="w-3 h-3" /> Add window
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <Button size="sm" icon={Save} loading={saving} onClick={save}>Save hours</Button>
        {msg && <div className="flex-1"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}
      </div>
    </div>
  );
}
